'use strict';

Object.defineProperty(exports, Symbol.toStringTag, { value: 'Module' });

const React = require('react');
const react = require('@emotion/react');
const styleEncapsulation = require('@zapier/style-encapsulation');
const utils_debounce = require('../../../../utils/debounce.cjs.js');
const utils_ensureArray = require('../../../../utils/ensureArray.cjs.js');
const theme_Colors = require('../../../../theme/Colors.cjs.js');
require('../../../../theme/Shadows.cjs.js');
require('../../../../theme/Typography.cjs.js');
const styleMixins_reset = require('../../../../style-mixins/reset.cjs.js');

const Styles = styleEncapsulation.labelStyles("ScrollSnap", {
  root: () => [
    react.css(Object.assign(Object.assign({}, styleMixins_reset.boxSizing), {
      width: "100%",
      // hides overflow columns for swiping interactions
      // minus a small overhang to indicate the ability to scroll
      overflow: "hidden"
    }))
  ],
  scrollableArea: () => [
    react.css(Object.assign(Object.assign({}, styleMixins_reset.boxSizing), {
      overflowX: "auto",
      overscrollBehaviorX: "none",
      // adds horizontal scroll snapping
      scrollSnapType: "x mandatory",
      WebkitOverflowScrolling: "touch",
      scrollBehavior: "smooth",
      // hides the scrollbar
      "&::-webkit-scrollbar": {
        display: "none"
      },
      scrollbarWidth: "none",
      "&:focus-visible": {
        outline: `2px solid ${theme_Colors.Colors.UiPrimaryStrongest}`,
        outlineOffset: "-2px"
      }
    }))
  ],
  scrollingSurface: (props, pageCount, alignment) => [
    react.css(Object.assign(Object.assign({}, styleMixins_reset.boxSizing), {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      // sets a fixed width based on number of pages + gap to create a wider scrolling surface
      width: pageCount * props.itemWidth + props.gap * Math.max(pageCount - 1, 0)
    })),
    alignment === "center" && styleEncapsulation.withModifier("centerAligned", {
      margin: "0 auto"
    })
  ],
  snappable: (props) => [
    react.css(Object.assign(Object.assign({}, styleMixins_reset.boxSizing), {
      // controls where the snapping aligns to on child elements
      scrollSnapAlign: props.scrollSnapAlign,
      width: props.itemWidth
    }))
  ]
});
const getSnapPoints = (opts) => {
  const { itemWidth, pageCount, scrollSnapAlign, gap } = opts;
  const snapPointGetter = {
    start: (_v, i) => (itemWidth + gap) * i,
    // the left side of each item
    center: (_v, i) => itemWidth / 2 + i * (itemWidth + gap / 2),
    // halfway between each item
    end: (_v, i) => itemWidth + i * (itemWidth + gap)
    // the right side of each item
  }[scrollSnapAlign];
  return Array.from({ length: pageCount }).map(snapPointGetter);
};
const getSnapThresholds = (opts) => {
  const { itemWidth, scrollSnapAlign, pageCount, gap, clientWidth } = opts;
  const snappingPoints = getSnapPoints(opts);
  const width = itemWidth + gap;
  const stoppingPoint = width * pageCount - clientWidth;
  const snapThresholdGetter = {
    start: (acc, _v, i, src) => {
      let point;
      if (i === 0) {
        point = 0;
      } else if (i === 1) {
        point = width / 2;
      } else if (i === src.length - 1) {
        point = width * src.length - (clientWidth - width);
      } else {
        point = acc[i - 1] + width;
      }
      if (point >= stoppingPoint) {
        const offset = Math.max((stoppingPoint - snappingPoints[i - 1]) / 2, 1);
        point = stoppingPoint - offset;
      }
      acc[i] = point;
      return acc;
    },
    center: (acc, _v, i, src) => {
      if (i === 0) {
        acc[i] = 0;
      } else if (i === 1) {
        acc[i] = width + width / 4;
      } else if (i === src.length - 1) {
        acc[i] = width * src.length - width * 1.25;
      } else {
        acc[i] = width * i;
      }
      return acc;
    },
    end: (acc, _v, i) => {
      if (i === 0) {
        acc[i] = 0;
      } else if (i === 1) {
        acc[i] = width * 1.75;
      } else {
        acc[i] = i * width + width / 2;
      }
      return acc;
    }
  }[scrollSnapAlign];
  return Array.from({ length: pageCount }).reduce(snapThresholdGetter, []);
};
const DEBOUNCE_MS = 50;
const ScrollSnap = ({ children, itemWidth, alignment = "left", gap = 0, onScroll = void 0, pageIndex = 0, scrollSnapAlign = "start" }) => {
  var _a;
  const childArray = utils_ensureArray.ensureArray(children);
  const pageCount = childArray.length;
  const rootRef = React.useRef(null);
  const clientWidth = ((_a = rootRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) || itemWidth;
  const scrollableAreaRef = React.useRef(null);
  const [currentPageIndex, setCurrentPageIndex] = React.useState(pageIndex);
  const snapThresholds = getSnapThresholds({
    pageCount,
    itemWidth,
    scrollSnapAlign,
    gap,
    clientWidth
  });
  const updateScrollableAreaDebounced = React.useCallback(utils_debounce.debounce((opts) => {
    var _a2, _b;
    if (!opts || !opts.scrollableAreaRef.current || !opts.rootRef.current) {
      return;
    }
    const scrollLeftOffset = {
      start: 0,
      center: (((_a2 = opts.rootRef.current) === null || _a2 === void 0 ? void 0 : _a2.clientWidth) || 0) / 2,
      end: ((_b = opts.rootRef.current) === null || _b === void 0 ? void 0 : _b.clientWidth) || 0
    }[opts.scrollSnapAlign];
    const snapPoints = getSnapPoints({
      pageCount: opts.pageCount,
      itemWidth: opts.itemWidth,
      scrollSnapAlign: opts.scrollSnapAlign,
      gap
    });
    if (opts.scrollableAreaRef.current) {
      opts.scrollableAreaRef.current.scrollLeft = snapPoints[opts.pageIndex] - scrollLeftOffset;
    }
  }, DEBOUNCE_MS), []);
  React.useEffect(() => {
    return updateScrollableAreaDebounced({
      itemWidth,
      pageCount,
      pageIndex,
      rootRef,
      scrollableAreaRef,
      scrollSnapAlign
    });
  }, [
    updateScrollableAreaDebounced,
    itemWidth,
    pageCount,
    pageIndex,
    rootRef,
    scrollableAreaRef,
    scrollSnapAlign
  ]);
  const onScrollInternal = () => {
    const { current } = scrollableAreaRef;
    const scrollLeft = (current === null || current === void 0 ? void 0 : current.scrollLeft) || 0;
    const cursor = {
      start: scrollLeft,
      center: scrollLeft + clientWidth / 2,
      end: scrollLeft + clientWidth
    }[scrollSnapAlign];
    const closestPoint = snapThresholds.reduce((acc, point) => cursor > point ? point : acc, 0);
    const closestPageIndex = snapThresholds.indexOf(closestPoint);
    updateScrollableAreaDebounced();
    if (onScroll && currentPageIndex !== closestPageIndex) {
      onScroll(closestPageIndex);
    }
    setCurrentPageIndex(closestPageIndex);
  };
  return react.jsx(
    "div",
    { css: Styles.root(), ref: rootRef, "data-zds": true },
    react.jsx(
      "div",
      { css: Styles.scrollableArea(), onScroll: onScrollInternal, ref: scrollableAreaRef, tabIndex: 0, "data-zds": true },
      react.jsx("div", { css: Styles.scrollingSurface({ itemWidth, gap }, pageCount, alignment), "data-zds": true }, childArray.map((child, i) => {
        return react.jsx("span", { css: Styles.snappable({ scrollSnapAlign, itemWidth }), key: `${i}-snappable`, "data-zds": true }, child);
      }))
    )
  );
};

exports.ScrollSnap = ScrollSnap;
exports.getSnapPoints = getSnapPoints;
exports.getSnapThresholds = getSnapThresholds;
//# sourceMappingURL=ScrollSnap.cjs.js.map
